import {
  Box,
  Button,
  Paper,
  Typography,
  RadioGroup,
  Radio,
  LinearProgress,
  Divider,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  getQuizById,
  startQuiz,
  submitQuizAttempt,
} from "../../api/quizAPI";
import GradientButton from "../Button";

/*
|--------------------------------------------------------------------------
| Render Rich Text
|--------------------------------------------------------------------------
|
| RichTextEditor stores HTML.
|
| Examples:
|
| H<sub>2</sub>O
| x<sup>2</sup>
| <img src="..." />
| <strong>Important</strong>
|
*/

const RichContent = ({
  html,
  className,
}) => {
  if (!html) return null;

  return (
    <Box
      className={className}
      sx={{
        "& p": {
          margin: 0,
          marginBottom: "8px",
        },

        "& img": {
          maxWidth: "100%",
          height: "auto",
          display: "block",
          marginTop: "8px",
          marginBottom: "8px",
          borderRadius: 1,
        },

        "& table": {
          maxWidth: "100%",
          borderCollapse: "collapse",
        },

        "& td, & th": {
          border: "1px solid",
          borderColor: "divider",
          padding: "6px 10px",
        },

        "& ul, & ol": {
          paddingLeft: "24px",
        },

        "& sub": {
          fontSize: "0.75em",
        },

        "& sup": {
          fontSize: "0.75em",
        },

        "& .ql-align-center": {
          textAlign: "center",
        },

        "& .ql-align-right": {
          textAlign: "right",
        },

        "& .ql-align-justify": {
          textAlign: "justify",
        },

        overflowWrap: "anywhere",
        wordBreak: "break-word",
      }}
      dangerouslySetInnerHTML={{
        __html: html,
      }}
    />
  );
};

/*
|--------------------------------------------------------------------------
| Normalize Question
|--------------------------------------------------------------------------
|
| Supports BOTH:
|
| 1. Old API structure
|
| {
|   QUESTION: "...",
|   "OPTION A": "...",
|   ...
| }
|
| 2. New structure
|
| {
|   question: {
|      html: "..."
|   },
|   options: [...]
| }
|
*/

const normalizeQuestion = (question) => {
  /*
   * New structure
   */
  if (
    question.question &&
    typeof question.question === "object"
  ) {
    return {
      id: question.id,

      questionHtml:
        question.question.html || "",

      options:
        (question.options || []).map(
          (option, index) => ({
            key: String.fromCharCode(
              65 + index
            ),

            html: option.html || "",

            isCorrect:
              Boolean(option.isCorrect),

            id: option.id,
          })
        ),

      time:
        Number(
          question.time ??
          question.TIME ??
          0
        ),
    };
  }

  /*
   * Old/current API structure
   */
  return {
    id: question.id,

    questionHtml:
      question["QUESTION"] || "",

    options: [
      "OPTION A",
      "OPTION B",
      "OPTION C",
      "OPTION D",
    ].map((key, index) => ({
      key: String.fromCharCode(
        65 + index
      ),

      html: question[key] || "",

      isCorrect:
        question["CORRECT OPTION"] === key,

      id: `${question.id}-${key}`,
    })),

    time:
      Number(question["TIME"]) || 0,
  };
};

export default function QuizAttempt() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState(null);

  const [currentIndex, setCurrentIndex] =
    useState(0);

  const [quizStarted, setQuizStarted] =
    useState(false);

  const [answers, setAnswers] =
    useState({});

  const [timeLeft, setTimeLeft] =
    useState(0);

  /*
  |--------------------------------------------------------------------------
  | Fetch Quiz
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        const res = await getQuizById(id);

        if (
          res.success &&
          res.data
        ) {
          setQuiz(res.data);

          if (
            res.data.questions?.length
          ) {
            const firstQuestion =
              normalizeQuestion(
                res.data.questions[0]
              );

            setTimeLeft(
              firstQuestion.time
            );
          }
        } else {
          console.error(
            "Invalid response:",
            res
          );
        }
      } catch (error) {
        console.error(
          "Failed to fetch quiz:",
          error
        );
      }
    };

    fetchQuiz();
  }, [id]);

  /*
  |--------------------------------------------------------------------------
  | Normalized Questions
  |--------------------------------------------------------------------------
  */

  const questions =
    quiz?.questions?.map(
      normalizeQuestion
    ) || [];

  const question =
    questions[currentIndex];

  /*
  |--------------------------------------------------------------------------
  | Timer
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (
      !quiz ||
      !quizStarted ||
      !question
    ) {
      return;
    }

    if (timeLeft <= 0) {
      handleNext();
      return;
    }

    const timer = setTimeout(() => {
      setTimeLeft(
        (prev) => prev - 1
      );
    }, 1000);

    return () =>
      clearTimeout(timer);
  }, [
    timeLeft,
    quizStarted,
    currentIndex,
    quiz,
  ]);

  /*
  |--------------------------------------------------------------------------
  | Prevent refresh
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const handleBeforeUnload = (
      e
    ) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener(
      "beforeunload",
      handleBeforeUnload
    );

    return () =>
      window.removeEventListener(
        "beforeunload",
        handleBeforeUnload
      );
  }, []);

  /*
  |--------------------------------------------------------------------------
  | Loading
  |--------------------------------------------------------------------------
  */

  if (!quiz) {
    return (
      <Typography>
        Loading quiz...
      </Typography>
    );
  }

  if (!question) {
    return (
      <Typography>
        No questions found.
      </Typography>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Select Answer
  |--------------------------------------------------------------------------
  */

  const handleSelect = (
    option
  ) => {
    setAnswers((prev) => ({
      ...prev,

      [question.id]:
        option.id,
    }));
  };

  /*
  |--------------------------------------------------------------------------
  | Next Question
  |--------------------------------------------------------------------------
  */

  const handleNext = async () => {
    if (currentIndex < questions.length - 1) {
      const nextIndex = currentIndex + 1;

      const nextQuestion = questions[nextIndex];

      setCurrentIndex(nextIndex);

      setTimeLeft(nextQuestion.time);
      return;
    }

    try {
      const response = await submitQuizAttempt(id, answers);

      if (!response.success) {
        alert("Unable to submit quiz");
        return;
      }

      navigate(`/quiz/${id}/result`, {
        state: {
          quiz,
          result: response.data,
        },
      });
    } catch (error) {
      console.error("Submit quiz error:", error);

      alert(
        error?.response?.data?.message ||
        "Failed to submit quiz"
      );
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Fullscreen
  |--------------------------------------------------------------------------
  */

  const enterFullscreen =
    async () => {
      try {
        if (
          !document.fullscreenElement
        ) {
          await document.documentElement.requestFullscreen();
        }
      } catch (err) {
        console.error(
          "Fullscreen error:",
          err
        );
      }
    };

  /*
  |--------------------------------------------------------------------------
  | START SCREEN
  |--------------------------------------------------------------------------
  */

  if (!quizStarted) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            borderRadius: 3,
            maxWidth: 520,
            width: "100%",
            boxShadow: (theme) =>
              theme.palette.mode ===
                "dark"
                ? "0px 10px 30px rgba(0,0,0,0.5)"
                : "0px 10px 30px rgba(0,0,0,0.15)",
          }}
        >
          <Typography
            variant="h5"
            mb={2}
          >
            {quiz.title}
          </Typography>

          <Typography
            variant="body2"
            mb={3}
            color="text.secondary"
          >
            Each question is timed
            individually.
            <br />
            Fullscreen mode will be
            enabled.
          </Typography>

          <GradientButton
            variant="contained"
            size="large"
            onClick={async () => {
              try {
                await startQuiz(id);

                await enterFullscreen();

                setQuizStarted(true);

                /*
                 * Make sure timer starts
                 * from current question.
                 */
                setTimeLeft(
                  question.time
                );
              } catch (err) {
                console.error(err);

                alert(
                  "Something went wrong"
                );
              }
            }}
          >
            Start Quiz
          </GradientButton>
        </Paper>
      </Box>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Current Selected Answer
  |--------------------------------------------------------------------------
  */

  const selectedAnswer =
    answers[question.id] || "";

  /*
  |--------------------------------------------------------------------------
  | Progress
  |--------------------------------------------------------------------------
  */

  const progress =
    question.time > 0
      ? Math.max(
        0,
        Math.min(
          100,
          (timeLeft /
            question.time) *
          100
        )
      )
      : 0;

  /*
  |--------------------------------------------------------------------------
  | QUIZ UI
  |--------------------------------------------------------------------------
  */
  const handleQuestionJump = (index) => {
    if (index < 0 || index >= questions.length) return;

    setCurrentIndex(index);
    setTimeLeft(questions[index].time);
  };


  /*
  |--------------------------------------------------------------------------
  | QUIZ UI — Two-column layout
  |--------------------------------------------------------------------------
  */

  const answeredCount = questions.filter(
    (q) => answers[q.id]
  ).length;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#EFF2FE",
        py: { xs: 2, md: 3 },
        px: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <Box
        sx={{
          maxWidth: "1600px",
          mx: "auto",
          width: "100%",
        }}
      >
        {/* HEADER */}
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            mb: 2,
            borderRadius: 3,
            color: "#fff",
            background: "linear-gradient(135deg, #0EA5E9, #2563EB)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700}>
                {quiz.title}
              </Typography>

              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Question {currentIndex + 1} of {questions.length}
              </Typography>
            </Box>

            <Box
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.18)",
                whiteSpace: "nowrap",
                fontWeight: 700,
              }}
            >
              ⏱ {timeLeft}s
            </Box>
          </Box>

          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              mt: 2,
              height: 7,
              borderRadius: 5,
              bgcolor: "rgba(255,255,255,0.3)",
              "& .MuiLinearProgress-bar": {
                bgcolor: "#fff",
              },
            }}
          />
        </Paper>

        {/* MAIN TWO-COLUMN LAYOUT */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "minmax(0, 1fr) 280px",
              lg: "minmax(0, 1fr) 320px",
            },
            gap: 2,
            alignItems: "start",
          }}
        >
          {/* LEFT: QUESTION AND OPTIONS */}
          <Paper
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 3,
              minWidth: 0,
            }}
          >
            <Typography
              variant="overline"
              color="primary.main"
              fontWeight={700}
            >
              QUESTION {currentIndex + 1}
            </Typography>

            <Box
              sx={{
                mt: 1,
                mb: 3,
                p: { xs: 2, md: 3 },
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflowWrap: "anywhere",
                minWidth: 0,
              }}
            >
              <RichContent html={question.questionHtml} />
            </Box>

            <Typography
              variant="subtitle1"
              fontWeight={700}
              mb={2}
            >
              Select the correct answer
            </Typography>

            <RadioGroup
              value={selectedAnswer}
              onChange={(e) => {
                const selected = question.options.find(
                  (option) => String(option.id) === e.target.value
                );

                if (selected) handleSelect(selected);
              }}
            >
              {question.options.map((option) => {
                const selected = selectedAnswer === option.id;

                return (
                  <Paper
                    key={option.id}
                    variant="outlined"
                    onClick={() => handleSelect(option)}
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      borderRadius: 2,
                      cursor: "pointer",
                      minWidth: 0,
                      border: "2px solid",
                      borderColor: selected ? "primary.main" : "divider",
                      bgcolor: selected ? "action.selected" : "transparent",
                      transition: "border-color 0.2s, background-color 0.2s",
                      "&:hover": {
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <Radio
                      checked={selected}
                      value={String(option.id)}
                      sx={{ mt: -0.5, ml: -0.5 }}
                    />

                    <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                      <Typography
                        component="span"
                        fontWeight={700}
                        sx={{ mr: 1 }}
                      >
                        {option.key}.
                      </Typography>

                      <RichContent html={option.html} />
                    </Box>
                  </Paper>
                );
              })}
            </RadioGroup>

            {/* QUESTION FOOTER */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                mt: 3,
                pt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="body2"
                color={selectedAnswer ? "success.main" : "text.secondary"}
              >
                {selectedAnswer
                  ? "✓ Answer selected"
                  : "Not answered"}
              </Typography>

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  disabled={currentIndex === 0}
                  onClick={() => handleQuestionJump(currentIndex - 1)}
                >
                  Previous
                </Button>

                <Button
                  variant="contained"
                  onClick={handleNext}
                >
                  {currentIndex === questions.length - 1
                    ? "Submit Quiz"
                    : "Next"}
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* RIGHT: QUESTION NAVIGATOR */}
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 3,
              position: { xs: "static", md: "sticky" },
              top: 16,
              minWidth: 0,
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              Question Palette
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, mb: 2 }}
            >
              Click any number to navigate.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                gap: 1,
              }}
            >
              {questions.map((q, index) => {
                const isCurrent = currentIndex === index;
                const isAnswered = Boolean(answers[q.id]);

                return (
                  <Button
                    key={q.id ?? index}
                    aria-label={`Go to question ${index + 1} `}
                    aria-current={isCurrent ? "step" : undefined}
                    variant="outlined"
                    onClick={() => handleQuestionJump(index)}
                    sx={{
                      minWidth: 0,
                      minHeight: 42,
                      p: 0.5,
                      borderRadius: 1.5,
                      fontWeight: 700,
                      color: isCurrent || isAnswered ? "#fff" : "text.primary",
                      bgcolor: isCurrent
                        ? "#2563EB"
                        : isAnswered
                          ? "#16A34A"
                          : "transparent",
                      borderColor: isCurrent
                        ? "#2563EB"
                        : isAnswered
                          ? "#16A34A"
                          : "divider",
                      outline: isCurrent ? "2px solid #93C5FD" : "none",
                      outlineOffset: 1,
                      "&:hover": {
                        bgcolor: isCurrent
                          ? "#1D4ED8"
                          : isAnswered
                            ? "#15803D"
                            : "action.hover",
                        borderColor: isCurrent || isAnswered
                          ? "transparent"
                          : "primary.main",
                      },
                    }}
                  >
                    {index + 1}
                  </Button>
                );
              })}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Answered
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {answeredCount} / {questions.length}
              </Typography>
            </Box>

            <LinearProgress
              variant="determinate"
              value={
                questions.length
                  ? (answeredCount / questions.length) * 100
                  : 0
              }
              sx={{ height: 6, borderRadius: 5, mb: 2 }}
            />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {[
                { color: "#2563EB", label: "Current question" },
                { color: "#16A34A", label: "Answered" },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Box
                    sx={{
                      width: 13,
                      height: 13,
                      borderRadius: 0.75,
                      bgcolor: item.color,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {item.label}
                  </Typography>
                </Box>
              ))}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 13,
                    height: 13,
                    borderRadius: 0.75,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Not answered
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );

}